import { StyleProvider } from '@varlet/ui';
import { readonly, ref, type Ref } from 'vue';

import { appStorageController, type AppStorageController } from '../app-storage';
import { darkTheme, lightTheme } from './brand-book';
import { ThemePreference } from './types';

enum ResolvedTheme {
  LIGHT = 'light',
  DARK = 'dark',
}

type ThemeController = {
  preference: Readonly<Ref<ThemePreference>>;
  resolvedTheme: Readonly<Ref<ResolvedTheme>>;
  dispose: () => void;
  init: () => void;
  setPreference: (preference: ThemePreference) => void;
};

const SYSTEM_THEME_MEDIA_QUERY: string = '(prefers-color-scheme: dark)';
const ThemeClass: Readonly<Record<ThemePreference, string>> = {
  [ThemePreference.SYSTEM]: 'theme-system',
  [ThemePreference.LIGHT]: 'theme-light',
  [ThemePreference.DARK]: 'theme-dark',
} as const;
const themeClasses: string[] = Object.values(ThemeClass);

const isBrowser = (): boolean => typeof document !== 'undefined' && typeof window !== 'undefined';

const createThemeController = (
  storageController: AppStorageController = appStorageController,
): ThemeController => {
  const preference = ref<ThemePreference>(ThemePreference.SYSTEM);
  const systemThemeMediaQuery = ref<MediaQueryList | null>(null);
  const resolvedTheme = ref<ResolvedTheme>(ResolvedTheme.LIGHT);
  let isInitialized: boolean = false;

  const resolveTheme = (): ResolvedTheme => {
    if (preference.value === ThemePreference.LIGHT) {
      return ResolvedTheme.LIGHT;
    }
    if (preference.value === ThemePreference.DARK) {
      return ResolvedTheme.DARK;
    }

    return systemThemeMediaQuery.value?.matches ? ResolvedTheme.DARK : ResolvedTheme.LIGHT;
  };

  const applyRootClass = (): void => {
    if (!isBrowser()) {
      return;
    }

    document.documentElement.classList.remove(...themeClasses);
    document.documentElement.classList.add(ThemeClass[preference.value]);
  };

  const applyVarletTheme = (): void => {
    StyleProvider(resolvedTheme.value === ResolvedTheme.DARK ? darkTheme : lightTheme);
  };

  const applyTheme = (): void => {
    resolvedTheme.value = resolveTheme();
    applyRootClass();
    applyVarletTheme();
  };

  const onSystemThemeChange = (): void => {
    if (preference.value === ThemePreference.SYSTEM) {
      resolvedTheme.value = resolveTheme();
      applyVarletTheme();
    }
  };

  const init = (): void => {
    if (isInitialized || !isBrowser()) {
      return;
    }

    preference.value = storageController.themePreference.value;
    systemThemeMediaQuery.value = window.matchMedia(SYSTEM_THEME_MEDIA_QUERY);
    systemThemeMediaQuery.value.addEventListener('change', onSystemThemeChange);
    isInitialized = true;
    applyTheme();
  };

  const dispose = (): void => {
    systemThemeMediaQuery.value?.removeEventListener('change', onSystemThemeChange);
    systemThemeMediaQuery.value = null;
    isInitialized = false;
  };

  const setPreference = (newPreference: ThemePreference): void => {
    preference.value = newPreference;
    storageController.setThemePreference(newPreference);
    applyTheme();
  };

  return {
    dispose,
    init,
    preference: readonly(preference),
    resolvedTheme: readonly(resolvedTheme),
    setPreference,
  };
};

const themeController: ThemeController = createThemeController();

export { createThemeController, ResolvedTheme, themeController, ThemePreference };
